import {
  Box,
  Flex,
  Grid,
  GridItem,
  IconButton,
  Text,
  Stack,
  Separator,
  Icon,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { mapClass } from "../../../services/Utils";
import { useEffect, useState } from "react";
import { useDashboardContext } from "../../../Contexts/DashboardContext";
import { useSchoolsContext } from "../../../Contexts/schoolContext";
import SchoolInfo from "./SchoolInfo";
import PeriodCard from "./PeriodCard";
import StatCard from "./StatCard";
import CompleteResultsChart from "./CompleteResultsChart";
import RecentStudentTable from "./RecentStudentTable";
import ClassPopulationChart from "./ClassPopulationChart";
import TopSubjectsChart from "./TopSubjectsChart";
import Empty from "../../CustomUi/Empty";
import LogoLoader from "../../CustomUi/LogoLoader";
import DashboardSetup from "./DashboardSetup";
import BgDark from "../../../assets/Default Cover Photo dark.jpg";
import BgLight from "../../../assets/Default Cover Photo light.jpg";
import useThemeColors from "../../../hooks/useThemeColors";
import { useStyles } from "../../../hooks/useStyles";
import useIcons from "../../../hooks/useIcons";
import { useColorModeValue } from "../../ui/color-mode";
import { Student } from "../../../services/Dtos";
import { useStudentsContext } from "../../../Contexts/StudentsContext";

const gridBorderRadius = "lg";

function DashboardGrid() {
  const { dashboard, isLoading: dashBoardIsLoading } = useDashboardContext();
  const { sessions, terms, school, levels, classes } = useSchoolsContext();
  const { setSelectedStudent } = useStudentsContext();
  const { noBarScrollBar } = useStyles();
  const icons = useIcons();

  const colors = useThemeColors();
  const coverBG = useColorModeValue(BgLight, BgDark);
  const blendMode = useColorModeValue("screen", "multiply");
  const overlay = useColorModeValue("whiteAlpha.700", "blackAlpha.500");

  const navigate = useNavigate();

  const gradientSchool = useColorModeValue(
    `radial-gradient(circle, {colors.${colors.schoolAccent.primary}.200} 45%, transparent 48%)`,
    `radial-gradient(circle, {colors.${colors.schoolAccent.primary}.800} 45%, transparent 48%)`
  );

  const gradientBrand = useColorModeValue(
    `radial-gradient(circle, {colors.blue.200} 45%,{colors.red.200} 45%, transparent 48%)`,
    `radial-gradient(circle, {colors.blue.800} 45%,{colors.red.800} 45%, transparent 48%)`
  );

  const [classMap, setClassMap] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (classes) {
      const map = mapClass(classes);
      setClassMap(map);
    }
  }, [classes]);

  const onClickStudent = (student: Student) => {
    setSelectedStudent(student);
    navigate("/students");
  };

  if (dashBoardIsLoading) {
    return <LogoLoader />;
  }

  return (
    <Box flex={1} h={"100%"}>
      <Grid
        //flex={1}
        h="100%"
        templateColumns={[
          "1fr",
          null,
          null,
          "repeat(6, 1fr)",
          "repeat(6, 1fr)",
        ]}
        gap={5}
      >
        <GridItem
          rowSpan={{ base: 4, sm: 2 }}
          colSpan={{ base: 0, lg: 4, xl: 4 }}
          bg={BgDark}
          borderRadius={gridBorderRadius}
          bgImage={`url(${school?.coverPhoto || coverBG})`}
          bgPos={"center"}
          backgroundSize="cover"
          pos={"relative"}
          shadow={"sm"}
        >
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg={colors.schoolAccent.primary + ".400"}
            opacity={0.8}
            mixBlendMode={blendMode}
            zIndex={0}
            borderRadius={gridBorderRadius}
          />

          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg={overlay}
            zIndex={0}
            borderRadius={gridBorderRadius}
          />
          <Flex h={"full"}>
            <SchoolInfo />
          </Flex>
        </GridItem>
        <GridItem
          h={"full"}
          rowSpan={{ base: 1, md: 2 }}
          colSpan={{ base: 0, lg: 2 }}
          borderRadius={gridBorderRadius}
          pos="relative"
          backdropFilter="blur(10px) saturate(180%)"
          border="1px solid"
          borderColor={colors.border.primary + "/30"}
          shadow={"sm"}
        >
          <Box
            borderRadius={gridBorderRadius}
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg={colors.background.glass}
            opacity={0.8}
            mixBlendMode={blendMode}
            zIndex={0}
            overflow="hidden"
            _before={{
              content: '""',
              position: "absolute",
              zIndex: 0,
              top: "50%",
              left: "50%",
              width: "350px",
              height: "350px",
              borderRadius: "full",
              transform: "translate(-80%, -60%)",
              // bgGradient: "to-r",
              // gradientFrom: accentGradient,
              // gradientTo: "transparent",
              filter: "blur(80px)",
              opacity: 0.7,
              bgImage: gradientSchool,
            }}
            _after={{
              content: '""',
              position: "absolute",
              zIndex: 0,
              bottom: "-150px",
              right: "-150px",
              width: "400px",
              height: "400px",
              borderRadius: "full",
              bgImage: gradientBrand,
              // bgGradient: "to-r",
              // gradientFrom: purpleGradient,
              // gradientTo: "transparent",
              filter: "blur(100px)",
              opacity: 0.8,
            }}
          />
          <Flex
            justifyContent={"space-between"}
            gap={5}
            alignItems={"center"}
            w={"full"}
            h={"full"}
            flexDir={["column", "column", "row", "row", "row"]}
            p={5}
            wrap={"wrap"}
          >
            <PeriodCard
              heading={
                sessions.find(
                  (session: { id: string }) =>
                    session.id === school?.currentSessionId
                )?.sessionName || ""
              }
              Subtitle="current session"
              icon={icons.main.session}
            />
            <PeriodCard
              heading={
                terms.find(
                  (term: { id: string }) => term.id === school?.currentTermId
                )?.termName || ""
              }
              Subtitle="current term"
              icon={icons.main.term}
            />
          </Flex>
        </GridItem>
        {sessions.length === 0 || terms.length === 0 || levels.length === 0 ? (
          <GridItem
            rowSpan={{ base: 1, md: 2 }}
            colSpan={{ base: 0, lg: 6 }}
            bg={colors.background.card}
            borderRadius={gridBorderRadius}
          >
            <Flex
              minH={"400px"}
              flexDir={["column", "column", "column", "column", "row"]}
            >
              <Stack flex={1} p={10}>
                <Text fontSize={"2xl"}>Welcome aboard! 🚀</Text>
                <Text fontSize={"sm"} mb={5}>
                  {"Before your new dashboard comes to life, let's set up a few things."}
                </Text>
                {!school?.avatar &&
                  !school?.accentColor &&
                  !school?.coverPhoto &&
                  !school?.footnote && (
                    <DashboardSetup
                      heading={"Customize School"}
                      Subtitle={
                        "Add school logo, cover photo, tagline, accent colour and more!"
                      }
                      backgroundColor={"cyan.600"}
                      iconColor={"white"}
                      icon={icons.main.school}
                      link={ "/admin/school"}
                    />
                  )}
                {sessions.length === 0 && (
                  <>
                    <Separator />
                    <DashboardSetup
                      heading={"Add Session"}
                      Subtitle={
                        "Start a new academic journey by adding a session!"
                      }
                      backgroundColor={"teal"}
                      iconColor={"white"}
                      icon={icons.main.session}
                      link={"/admin/academic/sessions"}
                    />
                  </>
                )}
                {terms.length === 0 && (
                  <>
                    <Separator />
                    <DashboardSetup
                      heading={"Add Term"}
                      Subtitle={
                        "Get started by adding new terms and setting the current term."
                      }
                      backgroundColor={"tomato"}
                      iconColor={"white"}
                      icon={icons.main.term}
                      link={"/admin/academic/terms"}
                    />
                  </>
                )}
                {levels.length === 0 && (
                  <>
                    <Separator />
                    <DashboardSetup
                      heading={"Add School Levels"}
                      Subtitle={
                        "Organize your school by adding new levels and managing them efficiently!"
                      }
                      backgroundColor={"blue.400"}
                      iconColor={"white"}
                      icon={icons.main.level}
                      link={"/admin/academic/levels"}
                    />
                  </>
                )}
                <Separator />
                <DashboardSetup
                  heading={"Add Users or Teachers"}
                  Subtitle={"Expand your team by adding new users or teachers!"}
                  backgroundColor={"orange.400"}
                  iconColor={"white"}
                  icon={icons.people.users}
                  link={"/admin/users"}
                />
              </Stack>
              <Flex justifyContent={"center"} flex={1} p={5}>
                <Empty />
              </Flex>
            </Flex>
          </GridItem>
        ) : (
          <>
            <GridItem
              rowSpan={{ base: 2, sm: 4, md: 1 }}
              colSpan={{ base: 0, lg: 4 }}
              borderRadius={gridBorderRadius}
              bg={colors.background.card}
              shadow={"sm"}
            >
              <Flex
                justifyContent={"space-between"}
                gap={5}
                alignItems={{ base: "flex-start", md: "center" }}
                w={"full"}
                h={"full"}
                flexDir={{ base: "column", md: "row" }}
                p={5}
                wrap={"wrap"}
              >
                <StatCard
                  icon={icons.people.students}
                  title={"Active Students"}
                  value={dashboard?.schoolInfo?.totalStudents || 0}
                />

                <StatCard
                  icon={icons.main.class}
                  title={"Classes"}
                  value={dashboard?.schoolInfo?.totalClasses || 0}
                  bg="rgba(254, 110, 75, 0.7)"
                  borderColor="rgb(254, 110, 75)"
                />
                <StatCard
                  icon={icons.main.subject}
                  title={"Subjects"}
                  value={dashboard?.schoolInfo?.totalSubjects || 0}
                  bg="rgba(256, 196, 62, 0.7)"
                  borderColor="rgb(256, 196, 62)"
                />
              </Flex>
            </GridItem>
            <GridItem
              rowSpan={{ base: 2, md: 1 }}
              colSpan={{ base: 0, lg: 2 }}
              borderRadius={gridBorderRadius}
              bg={colors.background.card}
              shadow={"sm"}
            >
              <Flex alignItems={"center"} p={5} pos={"relative"}>
                <CompleteResultsChart
                  max={dashboard?.results.totalResults || 0}
                  value={dashboard?.results.completedResults || 0}
                  isloading={dashBoardIsLoading}
                />
              </Flex>
            </GridItem>

            <GridItem
              rowSpan={{ base: 4, sm: 3 }}
              colSpan={{ base: 0, lg: 2 }}
              borderRadius={gridBorderRadius}
              bg={colors.background.card}
              shadow={"sm"}
              overflow={{ base: undefined, xl: "hidden" }}
            >
              <Flex p={5} flexDir={"column"} h={"full"}>
                <Flex
                  alignItems={"center"}
                  justifyContent={"space-between"}
                  mb={5}
                  gap={5}
                  h={7}
                >
                  <Text opacity={0.5}>Recently Added Students</Text>
                  <IconButton
                    aria-label="add student"
                    onClick={() => navigate("/students")}
                    variant={"subtle"}
                  >
                    <Icon as={icons.people.students} />
                  </IconButton>
                </Flex>
                <Flex h={"full"} overflow={"auto"} css={noBarScrollBar}>
                  <RecentStudentTable
                    students={dashboard?.recentStudents || []}
                    classMap={classMap}
                    onClickStudent={onClickStudent}
                  />
                </Flex>
              </Flex>
            </GridItem>

            <GridItem
              rowSpan={{ base: 4, sm: 3 }}
              colSpan={{ base: 0, lg: 2 }}
              borderRadius={gridBorderRadius}
              bg={colors.background.card}
              shadow={"sm"}
              h={"full"}
              overflow={{ base: undefined, xl: "hidden" }}
            >
              <Flex p={5} flexDir={"column"} h={"full"}>
                <Flex
                  alignItems={"center"}
                  justifyContent={"space-between"}
                  mb={5}
                  gap={5}
                  h={7}
                >
                  <Text opacity={0.5}>Top Classes by Population</Text>
                </Flex>

                <ClassPopulationChart
                  classData={dashboard?.classPopulation || null}
                />
              </Flex>
            </GridItem>

            <GridItem
              rowSpan={{ base: 4, sm: 3 }}
              colSpan={{ base: 0, lg: 2 }}
              borderRadius={gridBorderRadius}
              bg={colors.background.card}
              shadow={"sm"}
              h={"full"}
              overflow={{ base: undefined, xl: "hidden" }}
            >
              <Flex p={5} flexDir={"column"} h={"full"}>
                <Flex
                  alignItems={"center"}
                  justifyContent={"space-between"}
                  mb={5}
                  gap={5}
                  h={7}
                >
                  <Text opacity={0.5}>Top Subjects by Performance</Text>
                </Flex>
                <TopSubjectsChart
                  subjectData={dashboard?.subjectPerformance || null}
                />
              </Flex>
            </GridItem>
          </>
        )}
      </Grid>
    </Box>
  );
}

export default DashboardGrid;
